import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail, fileUrl, bapsPdfUrl } from "@/lib/api";
import { FileUploader } from "@/components/FileUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fmtRupiah } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Download, ShieldCheck, PenLine } from "lucide-react";

export function BapsPanel({ report, onChange }) {
  const r = report;
  const [parts, setParts] = useState(
    (r.baps_spare_parts || []).map((p) => ({ name: p.name, qty: p.qty, unit_price: p.unit_price }))
  );
  const [reason, setReason] = useState(r.baps_reason || "");
  const [signedFiles, setSignedFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(!r.baps_number);

  useEffect(() => {
    if (!editing) {
      setParts((r.baps_spare_parts || []).map((p) => ({ name: p.name, qty: p.qty, unit_price: p.unit_price })));
      setReason(r.baps_reason || "");
    }
    // eslint-disable-next-line
  }, [r.baps_number, r.baps_status]);

  const status = r.baps_status; // null | menunggu_ttd | ditandatangani
  const total = parts.reduce((s, p) => s + (Number(p.qty) || 0) * (Number(p.unit_price) || 0), 0);

  const saveBaps = async () => {
    const clean = parts.filter((p) => p.name?.trim());
    if (!clean.length) return toast.error("Tambahkan minimal satu sparepart");
    if (!reason.trim()) return toast.error("Alasan penggantian wajib diisi");
    setSaving(true);
    try {
      await api.post(`/reports/${r.id}/baps`, {
        spare_parts: clean.map((p) => ({
          name: p.name.trim(),
          qty: Number(p.qty) || 0,
          unit_price: Number(p.unit_price) || 0,
        })),
        reason: reason.trim(),
      });
      toast.success("BAPS tersimpan. Silakan cetak & tanda tangani.");
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveSigned = async () => {
    if (!signedFiles.length) return toast.error("Unggah dokumen BAPS yang sudah ditandatangani");
    setSaving(true);
    try {
      await api.post(`/reports/${r.id}/baps/sign`, { signed_file_id: signedFiles[0].id });
      toast.success("Dokumen bertanda tangan tersimpan");
      setSignedFiles([]);
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="baps-panel"
      className="rounded-md border border-cyan-200 bg-cyan-50/50 p-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 font-display text-sm font-semibold text-slate-900">
          <FileText className="h-4 w-4 text-cyan-700" />
          Berita Acara Penggantian Sparepart (BAPS)
        </h4>
        {r.baps_number && (
          <span className="font-num rounded-full bg-white px-3 py-1 text-xs font-semibold text-cyan-800 ring-1 ring-cyan-200">
            {r.baps_number}
          </span>
        )}
      </div>

      {status === "ditandatangani" ? (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="baps-signed-status">
          <ShieldCheck className="h-4 w-4" />
          BAPS sudah ditandatangani. Perbaikan dapat diselesaikan.
        </div>
      ) : status === "menunggu_ttd" ? (
        <p className="mt-2 text-xs text-amber-700" data-testid="baps-pending-status">
          Menunggu tanda tangan basah — cetak dokumen, tandatangani atasan &amp; bengkel, lalu unggah kembali.
        </p>
      ) : (
        <p className="mt-2 text-xs text-slate-500">
          Buat BAPS berisi daftar sparepart yang akan diganti sebagai dasar persetujuan sebelum perbaikan.
        </p>
      )}

      {/* Editable form */}
      {editing ? (
        <div className="mt-4 space-y-4">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label>Daftar Sparepart</Label>
              <Button
                data-testid="baps-add-part-btn"
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setParts([...parts, { name: "", qty: 1, unit_price: 0 }])}
              >
                <Plus className="mr-1 h-3 w-3" /> Tambah
              </Button>
            </div>
            <div className="space-y-2">
              {parts.map((p, i) => (
                <div key={i} className="flex gap-2" data-testid={`baps-part-row-${i}`}>
                  <Input
                    data-testid={`baps-part-name-${i}`}
                    placeholder="Nama sparepart"
                    value={p.name}
                    onChange={(e) => {
                      const c = [...parts];
                      c[i].name = e.target.value;
                      setParts(c);
                    }}
                  />
                  <Input
                    data-testid={`baps-part-qty-${i}`}
                    className="w-20"
                    type="number"
                    min={0}
                    placeholder="Qty"
                    value={p.qty}
                    onChange={(e) => {
                      const c = [...parts];
                      c[i].qty = e.target.value;
                      setParts(c);
                    }}
                  />
                  <Input
                    data-testid={`baps-part-price-${i}`}
                    className="w-32"
                    type="number"
                    min={0}
                    placeholder="Harga satuan"
                    value={p.unit_price}
                    onChange={(e) => {
                      const c = [...parts];
                      c[i].unit_price = e.target.value;
                      setParts(c);
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setParts(parts.filter((_, x) => x !== i))}
                  >
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </div>
              ))}
              {parts.length === 0 && (
                <p className="text-xs text-slate-400">Belum ada sparepart.</p>
              )}
            </div>
            <p className="mt-2 text-right text-sm font-semibold text-slate-700">
              Total: <span className="font-num">{fmtRupiah(total)}</span>
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Alasan Penggantian</Label>
            <Textarea
              data-testid="baps-reason"
              rows={3}
              placeholder="Jelaskan alasan penggantian sparepart…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="baps-save-btn"
              className="bg-cyan-700 hover:bg-cyan-800"
              disabled={saving}
              onClick={saveBaps}
            >
              {r.baps_number ? "Perbarui BAPS" : "Buat BAPS"}
            </Button>
            {r.baps_number && (
              <Button variant="outline" onClick={() => setEditing(false)} disabled={saving}>
                Batal
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {/* Read-only summary */}
          <div className="rounded-md border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs text-slate-500">
                  <th className="px-3 py-2">Sparepart</th>
                  <th className="px-3 py-2 text-center">Qty</th>
                  <th className="px-3 py-2 text-right">Harga</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(r.baps_spare_parts || []).map((p, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid={`baps-summary-row-${i}`}>
                    <td className="px-3 py-2 text-slate-700">{p.name}</td>
                    <td className="px-3 py-2 text-center font-num">{p.qty}</td>
                    <td className="px-3 py-2 text-right font-num">{fmtRupiah(p.unit_price)}</td>
                    <td className="px-3 py-2 text-right font-num">{fmtRupiah(p.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.baps_reason && (
            <p className="text-sm text-slate-600">
              <span className="text-xs font-semibold uppercase text-slate-400">Alasan: </span>
              {r.baps_reason}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="baps-download-btn"
              variant="outline"
              onClick={() => window.open(bapsPdfUrl(r.id), "_blank")}
            >
              <Download className="mr-1 h-4 w-4" /> Cetak / Unduh BAPS
            </Button>
            {status !== "ditandatangani" && (
              <Button data-testid="baps-edit-btn" variant="ghost" onClick={() => setEditing(true)}>
                <PenLine className="mr-1 h-4 w-4" /> Edit BAPS
              </Button>
            )}
            {r.baps_signed_file_id && (
              <a
                href={fileUrl(r.baps_signed_file_id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
                data-testid="baps-view-signed"
              >
                <FileText className="mr-1 h-4 w-4" /> Lihat dokumen tertanda tangan
              </a>
            )}
          </div>

          {status !== "ditandatangani" && (
            <div className="space-y-2 rounded-md border border-dashed border-slate-300 p-3">
              <Label>Unggah Dokumen BAPS Bertanda Tangan Basah (foto/scan/PDF)</Label>
              <FileUploader
                files={signedFiles}
                setFiles={setSignedFiles}
                testId="baps-signed-uploader"
                label="Unggah scan"
                accept="image/*,application/pdf"
              />
              <Button
                data-testid="baps-save-signed-btn"
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={saving || !signedFiles.length}
                onClick={saveSigned}
              >
                Simpan Dokumen Tertanda Tangan
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
